import { Logger } from 'utilities';
import type {
  IndexedDBStorageManager,
  IndexedDBStorageManagerOptions,
  IndexedDBStorageRecord,
} from './indexedDBStorageManager.types';

const idbRequest = <T,>(req: IDBRequest<T>): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

export const createIndexedDBStorageManager = ({
  dbName,
  dbVersion,
  storeName,
  fallbackStorageKey,
  logNamespace,
}: IndexedDBStorageManagerOptions): IndexedDBStorageManager => {
  let dbPromise: Promise<IDBDatabase> | null = null;
  const memoryFallback = new Map<string, unknown>();

  const fallbackKeyFor = (key: string): string =>
    typeof fallbackStorageKey === 'function' ? fallbackStorageKey(key) : fallbackStorageKey;

  const openDB = (): Promise<IDBDatabase> => {
    if (dbPromise) {
      return dbPromise;
    }

    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        dbPromise = null;
        reject(new Error('IndexedDB not available'));
        return;
      }

      const req = indexedDB.open(dbName, dbVersion);
      req.onupgradeneeded = e => {
        const db = (e.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: 'key' });
        }
      };
      req.onsuccess = e => resolve((e.target as IDBOpenDBRequest).result);
      req.onerror = () => {
        dbPromise = null;
        reject(req.error);
      };
    });

    return dbPromise;
  };

  const getFallback = <T,>(key: string): T | undefined => {
    if (memoryFallback.has(key)) {
      return memoryFallback.get(key) as T;
    }

    if (typeof window === 'undefined') {
      return undefined;
    }

    try {
      const raw = window.localStorage.getItem(fallbackKeyFor(key));
      return raw ? (JSON.parse(raw) as T) : undefined;
    } catch (error) {
      Logger.debug(`${logNamespace} fallback read failed:`, error);
      return undefined;
    }
  };

  const setFallback = (key: string, value: unknown): void => {
    memoryFallback.set(key, value);

    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(fallbackKeyFor(key), JSON.stringify(value));
    } catch (error) {
      Logger.debug(`${logNamespace} fallback write failed; using memory fallback:`, error);
    }
  };

  const removeFallback = (key: string): void => {
    memoryFallback.delete(key);

    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.removeItem(fallbackKeyFor(key));
    } catch (error) {
      Logger.debug(`${logNamespace} fallback removal failed:`, error);
    }
  };

  return {
    get: async <T,>(key: string) => {
      try {
        const db = await openDB();
        const tx = db.transaction(storeName, 'readonly');
        const record = await idbRequest<IndexedDBStorageRecord<T> | undefined>(
          tx.objectStore(storeName).get(key)
        );
        return record?.value;
      } catch (error) {
        Logger.debug(`${logNamespace} IndexedDB read failed; using fallback:`, error);
        return getFallback<T>(key);
      }
    },
    set: async (key: string, value: unknown) => {
      setFallback(key, value);

      try {
        const db = await openDB();
        const tx = db.transaction(storeName, 'readwrite');
        await idbRequest(
          tx.objectStore(storeName).put({
            key,
            value,
            updatedAt: Date.now(),
          } satisfies IndexedDBStorageRecord)
        );
        removeFallback(key);
      } catch (error) {
        Logger.debug(`${logNamespace} IndexedDB write failed; fallback remains active:`, error);
      }
    },
    remove: async (key: string) => {
      removeFallback(key);

      try {
        const db = await openDB();
        const tx = db.transaction(storeName, 'readwrite');
        await idbRequest(tx.objectStore(storeName).delete(key));
      } catch (error) {
        Logger.debug(`${logNamespace} IndexedDB removal failed:`, error);
      }
    },
  };
};
